<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { useRoute } from 'vue-router';

type ScrollPos = { left: number; top: number };
const LS_KEY = 'itp/router/scroll';

const route = useRoute();
const scrollerEl = ref<HTMLElement | null>(null);
let isRestoring = false;

function readMap(): Record<string, ScrollPos> {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '{}') as Record<
      string,
      ScrollPos
    >;
  } catch (error) {
    console.error('Failed to load scroll positions from storage:', error);
    return {};
  }
}
function writeMap(map: Record<string, ScrollPos>) {
  localStorage.setItem(LS_KEY, JSON.stringify(map));
}
function keyOf(fullPath: string) {
  return fullPath || '/';
}

const scrollCache: Record<string, ScrollPos> = readMap();
let writeScheduled = false;

function scheduleWriteSoon() {
  if (writeScheduled) {
    return;
  }
  writeScheduled = true;
  requestAnimationFrame(() => {
    writeMap(scrollCache);
    writeScheduled = false;
  });
}

function handleCacheReset() {
  isRestoring = true;

  for (const key in scrollCache) {
    delete scrollCache[key];
  }
  scrollerEl.value?.scrollTo(0, 0);

  writeMap(scrollCache);

  queueMicrotask(() => {
    isRestoring = false;
  });
}

function saveScroll(forFullPath: string) {
  if (isRestoring) {
    return;
  }

  const scrollerElement = scrollerEl.value;
  if (!scrollerElement) {
    return;
  }

  const key = keyOf(forFullPath);
  const next: ScrollPos = {
    left: scrollerElement.scrollLeft,
    top: scrollerElement.scrollTop,
  };
  const prev = scrollCache[key];

  const contentTooShort =
    scrollerElement.scrollHeight <= scrollerElement.clientHeight;
  if (prev && prev.top > 0 && next.top === 0 && contentTooShort) {
    return;
  }

  scrollCache[key] = next;
  scheduleWriteSoon();
}

async function restoreScroll(forFullPath: string) {
  const scrollerElement = scrollerEl.value;
  if (!scrollerElement) {
    return;
  }

  const wanted = scrollCache[keyOf(forFullPath)] ?? { left: 0, top: 0 };

  isRestoring = true;
  await nextTick();

  let tries = 0;
  const maxTries = 15;

  const tryOnce = () => {
    if (!scrollerEl.value) {
      return true;
    }

    const maxTop = Math.max(
      0,
      scrollerEl.value.scrollHeight - scrollerEl.value.clientHeight,
    );
    const maxLeft = Math.max(
      0,
      scrollerEl.value.scrollWidth - scrollerEl.value.clientWidth,
    );

    const readyTop = wanted.top <= 0 || maxTop >= wanted.top - 1;
    const readyLeft = wanted.left <= 0 || maxLeft >= wanted.left - 1;

    if (readyTop && readyLeft) {
      scrollerEl.value.scrollTo(
        Math.min(wanted.left, maxLeft),
        Math.min(wanted.top, maxTop),
      );
      return true;
    }
    return false;
  };

  const tick = (resolve: () => void) => {
    requestAnimationFrame(() => {
      tries++;
      if (tryOnce() || tries >= maxTries) {
        resolve();
      } else {
        tick(resolve);
      }
    });
  };

  if (!tryOnce()) {
    await new Promise<void>((res) => tick(res));
  }

  queueMicrotask(() => {
    isRestoring = false;
  });
}

function onScroll() {
  saveScroll(route.fullPath);
}
function onPageHide() {
  saveScroll(route.fullPath);
  writeMap(scrollCache);
}

onMounted(async () => {
  await restoreScroll(route.fullPath);

  scrollerEl.value?.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('pagehide', onPageHide);

  window.addEventListener('itp:state-reset', handleCacheReset);
});

onBeforeUnmount(() => {
  saveScroll(route.fullPath);
  writeMap(scrollCache);
  scrollerEl.value?.removeEventListener('scroll', onScroll);
  window.removeEventListener('pagehide', onPageHide);

  window.removeEventListener('itp:state-reset', handleCacheReset);
});

watch(
  () => route.fullPath,
  async (nextFullPath, prevFullPath) => {
    if (prevFullPath) {
      saveScroll(prevFullPath);
    }
    await restoreScroll(nextFullPath);
    scrollerEl.value?.focus({ preventScroll: true });
  },
);
</script>

<template>
  <div ref="scrollerEl" class="itp-scroll" tabindex="-1">
    <div class="container py-3">
      <RouterView />
    </div>
  </div>
</template>
